import { Cookies } from 'react-cookie';

export interface ICacheService {

  get<T>(key: string): T | null;
  getAsync<T>(key: string): Promise<T | null>;
  addOrUpdate<T>(key: string, value: T): void;
  addOrUpdateAsync<T>(key: string, value: T): Promise<void>;
  remove(key: string): void;
  removeAsync(key: string): Promise<void>;
  removeAll(): void;
  removeAllAsync(): Promise<void>;

  getSession<T>(key: string): T | null;
  getSessionAsync<T>(key: string): Promise<T | null>;
  addOrUpdateSession<T>(key: string, value: T): void;
  addOrUpdateSessionAsync<T>(key: string, value: T): Promise<void>;
  removeSession(key: string): void;
  removeSessionAsync(key: string): Promise<void>;
  removeAllSession(): void;
  removeAllSessionAsync(): Promise<void>;

  getCookie<T>(key: string): T | null;
  getCookieAsync<T>(key: string): Promise<T | null>;
  addOrUpdateCookie<T>(key: string, value: T): void;
  addOrUpdateCookieAsync<T>(key: string, value: T): Promise<void>;
  removeCookie(key: string): void;
  removeCookieAsync(key: string): Promise<void>;
  removeAllCookie(): void;
  removeAllCookieAsync(): Promise<void>;

  removeAllCache(): void;
  removeAllCacheAsync(): Promise<void>;
}

export class CacheService implements ICacheService {

  private static _instance: CacheService;
  public static get instance() {
    return this._instance ?? (this._instance = new CacheService());
  }

  private _useEncoding: boolean = true;
  public get<T>(key: string): T | null {
    let item = localStorage.getItem(key);
    if (item === null) { return null; }
    let decoded = this.decode(item);
    try {
      return JSON.parse(decoded) as T
    } catch {
      return decoded as T
    }
  }

  public async getAsync<T>(key: string): Promise<T | null> {
    let item = localStorage.getItem(key);
    if (item === null) { return null; }
    let decoded = this.decode(item);
    return Promise.resolve().then(function () {
      try {
        return JSON.parse(decoded) as T
      } catch {
        return decoded as T
      }
    });
  }

  public addOrUpdate<T>(key: string, value: T): void {
    let item = this.encode(value?.toString())
    localStorage.setItem(key, item);
  }

  public async addOrUpdateAsync<T>(key: string, value: T): Promise<void> {
    let item = this.encode(value?.toString())
    return Promise.resolve().then(function () {
      localStorage.setItem(key, item);
    });
  }

  public remove(key: string): void {
    localStorage.removeItem(key);
  }

  public async removeAsync(key: string): Promise<void> {
    return Promise.resolve().then(function () {
      localStorage.removeItem(key);
    });
  }

  public removeAll(): void {
    localStorage.clear();
  }

  public async removeAllAsync(): Promise<void> {
    return Promise.resolve().then(function () {
      localStorage.clear();
    });
  }

  public getSession<T>(key: string): T | null {
    let item = sessionStorage.getItem(key);
    if (item === null) { return null; }
    let decoded = this.decode(item);
    try {
      return JSON.parse(decoded) as T
    } catch {
      return decoded as T
    }
  }

  public async getSessionAsync<T>(key: string): Promise<T | null> {
    let item = sessionStorage.getItem(key);
    if (item === null) { return null; }
    let decoded = this.decode(item);
    return Promise.resolve().then(function () {
      try {
        return JSON.parse(decoded) as T
      } catch {
        return decoded as T
      }
    });
  }

  public addOrUpdateSession<T>(key: string, value: T): void {
    let item = this.encode(value?.toString())
    sessionStorage.setItem(key, item);
  }

  public async addOrUpdateSessionAsync<T>(key: string, value: T): Promise<void> {
    let item = this.encode(value?.toString())
    return Promise.resolve().then(function () {
      sessionStorage.setItem(key, item);
    });
  }

  public removeSession(key: string): void {
    sessionStorage.removeItem(key);
  }

  public async removeSessionAsync(key: string): Promise<void> {
    return Promise.resolve().then(function () {
      sessionStorage.removeItem(key);
    });
  }

  public removeAllSession(): void {
    sessionStorage.clear();
  }

  public async removeAllSessionAsync(): Promise<void> {
    return Promise.resolve().then(function () {
      sessionStorage.clear();
    });
  }

  public getCookie<T>(key: string): T | null {
    const cookies = new Cookies();
    const item = cookies.get(key);
    if (item === null) { return null; }
    let decoded = this.decode(item);
    try {
      return JSON.parse(decoded) as T
    } catch {
      return decoded as T
    }
  }

  public async getCookieAsync<T>(key: string): Promise<T | null> {
    const cookies = new Cookies();
    const item = cookies.get(key);
    if (item === null) { return null; }
    let decoded = this.decode(item);
    return Promise.resolve().then(function () {
      try {
        return JSON.parse(decoded) as T
      } catch {
        return decoded as T
      }
    });
  }

  public addOrUpdateCookie<T>(key: string, value: T): void {
    let item = this.encode(value?.toString())
    const cookies = new Cookies();
    cookies.set(key, item, {
      secure: true,
      httpOnly: false,
      sameSite: 'strict',
    });
  }

  public async addOrUpdateCookieAsync<T>(key: string, value: T): Promise<void> {
    let item = this.encode(value?.toString())
    return Promise.resolve().then(function () {
      const cookies = new Cookies();
      cookies.set(key, item, {
        secure: true,
        httpOnly: false,
        sameSite: 'strict',
      });
    });

  }

  public removeCookie(key: string): void {
    const cookies = new Cookies();
    cookies.remove(key)
  }

  public async removeCookieAsync(key: string): Promise<void> {
    return Promise.resolve().then(function () {
      const cookies = new Cookies();
      cookies.remove(key)
    });
  }

  public removeAllCookie(): void {
    const cookies = new Cookies();
    let items = cookies.getAll();
    //TODO: Remove All
  }

  public async removeAllCookieAsync(): Promise<void> {
    return Promise.resolve().then(function () {

    });
  }

  public removeAllCache(): void {
    this.removeAll();
    this.removeAllSession();
    this.removeAllCookie();
  }

  public async removeAllCacheAsync(): Promise<void> {
    await this.removeAllAsync();
    await this.removeAllSessionAsync();
    await this.removeAllCookieAsync();
  }

  private encode(text?: string): string {
    if (!text) {
      return "";
    }
    if (this._useEncoding) {
      return btoa(text)
    }
    return text;
  }

  private decode(text?: string): string {
    if (!text) {
      return "";
    }
    if (this._useEncoding) {
      return atob(text)
    }
    return text;
  }
}

